// Intl.RelativeTimeFormat polyfill
import "@formatjs/intl-locale/polyfill";
import "@formatjs/intl-relativetimeformat/polyfill";
import "@formatjs/intl-relativetimeformat/locale-data/en";
import "@formatjs/intl-relativetimeformat/locale-data/ja";
import "@formatjs/intl-relativetimeformat/locale-data/zh";

// Intl.PluralRules polyfill
import "@formatjs/intl-pluralrules/polyfill";
import "@formatjs/intl-pluralrules/locale-data/en";
import "@formatjs/intl-pluralrules/locale-data/ja";
import "@formatjs/intl-pluralrules/locale-data/zh";

const timeAgoFormatters = {
  en: new Intl.RelativeTimeFormat("en", { style: "short" }),
  ja: new Intl.RelativeTimeFormat("ja"),
  zh: new Intl.RelativeTimeFormat("zh"),
};

const numberFormatters = {
  en: new Intl.NumberFormat("en", { notation: "compact", minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  ja: new Intl.NumberFormat("ja", { notation: "compact", minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  zh: new Intl.NumberFormat("zh", { notation: "compact", minimumFractionDigits: 1, maximumFractionDigits: 1 }),
};

const longNumberFormatters = {
  en: new Intl.NumberFormat("en"),
  ja: new Intl.NumberFormat("ja"),
  zh: new Intl.NumberFormat("zh"),
};

import {
  // Roles
  USER_ROLE_ANONYMOUS,
  USER_ROLE_JANITOR,
  USER_ROLE_MODERATOR,
  USER_ROLE_OFFICER,
  USER_ROLE_ADMIN,

  // States
  USER_STATUS_ACTIVE,
  USER_STATUS_BANNED,

  // Times
  SECONDS_PER_MINUTE,
  SECONDS_PER_HOUR,
  SECONDS_PER_DAY,
  SECONDS_PER_MONTH,
  SECONDS_PER_YEAR,
} from "utils/constants";

export function isLoggedIn(user) {
  return user?.role !== USER_ROLE_ANONYMOUS;
}

export function isActive(user) {
  return user?.status === USER_STATUS_ACTIVE;
}

export function isBanned(user) {
  return user?.status === USER_STATUS_BANNED;
}

export function isModerator(user) {
  return [USER_ROLE_MODERATOR, USER_ROLE_OFFICER].includes(user?.role);
}

export function isOfficer(user) {
  return user?.role === USER_ROLE_OFFICER;
}

export function isAdmin(user) {
  return user?.role === USER_ROLE_ADMIN;
}

export function isSuperUser(user) {
  return [USER_ROLE_JANITOR, USER_ROLE_MODERATOR, USER_ROLE_OFFICER, USER_ROLE_ADMIN].includes(user?.role);
}

export function isPremium(user) {
  return user?.premium === true;
}

export function isOnline(date) {
  if (!date) return false;

  const now = Date.now();
  const then = Number(new Date(date));
  const delta = (now - then) / 1000;
  return delta <= 15 * SECONDS_PER_MINUTE;
}

export function needsCaptcha(user) {
  return !isSuperUser(user);
}

export function formatNumber(number, locale) {
  if (number < 1000) {
    return formatLongNumber(number, locale);
  }

  const formatter = numberFormatters[locale] ?? numberFormatters["en"];
  return formatter.format(number);
}

export function formatLongNumber(number, locale) {
  const formatter = longNumberFormatters[locale] ?? longNumberFormatters["en"];
  return formatter.format(number);
}

export function formatMonth(month, locale) {
  const formatter = new Intl.DateTimeFormat(locale, { month: "short" });
  return formatter.format(new Date(1970, month - 1, 2));
}

export function padNumber(number) {
  return number < 10 ? `0${number}` : `${number}`;
}

export function formatDuration(duration) {
  const min = parseInt(duration / 60, 10);
  const sec = duration % 60;

  return `${min}:${sec > 9 ? sec : "0" + sec}`;
}

export function formatTimeAgo(datetime, locale) {
  try {
    const formatter = timeAgoFormatters[locale] ?? timeAgoFormatters["en"];

    // Get delta in seconds between now and then
    const now = new Date();
    const then = new Date(datetime);
    const delta = (now - then) / 1000;
    const absDelta = Math.abs(delta);

    // Get how much passed of each unit
    const minutes = Math.floor(absDelta / SECONDS_PER_MINUTE);
    const hours = Math.floor(absDelta / SECONDS_PER_HOUR);
    const days = Math.floor(absDelta / SECONDS_PER_DAY);
    const months = Math.floor(absDelta / SECONDS_PER_MONTH);
    const years = Math.floor(absDelta / SECONDS_PER_YEAR);

    let unit = "seconds";
    let value = Math.floor(absDelta);

    if (years > 0) {
      unit = "years";
      value = years;
    } else if (months > 0) {
      unit = "months";
      value = months;
    } else if (days > 0) {
      unit = "days";
      value = days;
    } else if (hours > 0) {
      unit = "hours";
      value = hours;
    } else if (minutes > 0) {
      unit = "minutes";
      value = minutes;
    }

    return formatter.format(delta < 0 ? value : -value, unit);
  } catch (err) {
    console.log("Formatting error", datetime, locale, err);
    return "N/A";
  }
}

export function formatTime(locale, datetime) {
  const date = new Date(datetime);
  return date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatFullDate(datetime, locale) {
  const date = new Date(datetime);

  return new Intl.DateTimeFormat(navigator.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
    timeZoneName: "short",
  }).format(date);
}

export function formatDate(datetime) {
  const date = new Date(datetime);

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1, 2);
  const day = pad(date.getDate(), 2);

  return `${year}-${month}-${day}`;
}

export function formatDateAndTime(datetime, locale) {
  const date = new Date(datetime);
  return date.toLocaleString(locale);
}

export function formatCurrency(currency, value, locale) {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return formatter.format(value);
}

export function formatUsername(username) {
  return username;
}

// Uniquely filter array of objects by their keys
//   Given array [{ id: 1 }, { id: 1 }, { id: 2}]
//   returns [{ id: 1 }, { id: 2}]
//   when called with f(arr, "id")
export function uniqueArrayByKey(arr, key) {
  const keys = [];
  const result = [];

  arr.forEach(item => {
    if (keys.includes(item[key]) === false) {
      result.push(item);
      keys.push(item[key]);
    }
  });

  return result;
}

export function sortByKey(key, arr) {
  return arr.sort((a, b) => {
    if (a[key] === b[key]) {
      return 0;
    } else if (a[key] < b[key]) {
      return -1;
    } else {
      return 1;
    }
  });
}

// parse  ?key=value&hello=world
// into   { key: "value", hello: "world" }
export function parseQueryParams(query, defaultValues = {}) {
  const params = new URLSearchParams(query);
  const result = [...params.entries()].reduce((all, [key, value]) => {
    all[key] = value;
    return all;
  }, defaultValues);

  return result;
}

export function filtersToQueryParams(filters, defaultValues = {}) {
  return Object.keys(filters).reduce((all, key) => {
    let value = filters[key];

    // Don't include empty values
    if (value?.length === 0 || value === defaultValues?.[key]) {
      return all;
    }

    // Tags want their IDs
    if (key === "tags") {
      value = value.map(tag => tag.id);
    }

    return { ...all, [key]: value };
  }, {});
}

export function queryToFilters(query, defaultValues = {}) {
  const params = parseQueryParams(query, defaultValues);

  if (typeof params?.tags === "string") {
    params.tags = (params?.tags?.split(",") ?? []).map(tag => ({ id: tag }));
  }

  return params;
}

function pad(str, target = 2) {
  let result = str.toString();

  if (result.length < target) {
    const padding = Array(target - result.length).fill("0").join("");
    result = `${padding}${result}`;
  }

  return result;
}

export async function sha1(str) {
  const msgUint8 = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-1", msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
}

export function sortVideoQuality(a, b) {
  if (a.name === "Source" || a.name > b.name) {
    return -1;
  } else if (b.name === "Source" || b.name > a.name) {
    return 1;
  }

  return 0;
}

const YOUTUBE_HOSTS = ["www.youtube.com", "youtu.be"];
const URL_REGEX = /https:\/\//;

export function parseEmbedUrl(url) {
  let site = null;
  let id = null;

  try {
    if (!URL_REGEX.test(url)) {
      url = `https://${url}`;
    }

    const parsed = new URL(url);
    const params = new URLSearchParams(parsed.search);

    if (YOUTUBE_HOSTS.includes(parsed.host)) {
      site = "youtube";

      // Styled ?v=VIDEO_ID
      if (params.get("v") !== null) {
        id = params.get("v");

        // Short-link youtu.be/VIDEO_ID
      } else {
        id = parsed?.pathname?.split("/")?.[1] ?? null;
      }
    }
  } catch (err) {
    console.log("Failed to parse URL", url);
  }

  return [site, id];
}

export function anyToHash(item) {
  return btoa(JSON.stringify(item));
}


const ZH_REGEX = /\p{sc=Han}/gu;
const JA_REGEX = /[ぁ-んァ-ン+]/g;

export function guessTextLanguage(text) {
  if (typeof text !== "string" || text.length === 0) {
    return null;
  }

  const textLength = text.length;
  const zhMatchCount = [...text.matchAll(ZH_REGEX)].length;
  const zhMatchPercent = zhMatchCount / textLength;
  const jaMatchCount = [...text.matchAll(JA_REGEX)].length;
  const jaMatchPercent = jaMatchCount / textLength;

  if (zhMatchCount !== 0 && zhMatchCount > jaMatchCount && zhMatchPercent >= 0.1) {
    return "zh";
  } else if (jaMatchCount !== 0 && jaMatchCount > zhMatchCount && jaMatchPercent >= 0.1) {
    return "ja";
  }

  return "en";
}
